"use client";

import type React from "react";
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Link as ChakraLink,
  Flex,
  Button,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box minH="100vh" bg="white">
      {/* Navigation */}
      <Box
        as="nav"
        position="sticky"
        top={0}
        zIndex={10}
        bg="white"
        borderBottom="1px"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <Container maxW="7xl" py={4}>
          <Flex justify="space-between" align="center">
            <Box
              as="img"
              src={process.env.REACT_APP_MAIN_LOGO_URL}
              alt="App Logo"
              h="50px"
              cursor="pointer"
              onClick={() => navigate("/")}
            />
            <Button
              variant="brandGradient"
              size="md"
              onClick={() => navigate("/signin")}
            >
              Login
            </Button>
          </Flex>
        </Container>
      </Box>

      <Container maxW="4xl" py={12}>
        <VStack spacing={8} align="start">
          <Heading as="h1" fontSize="4xl">
            Privacy Policy
          </Heading>

          <VStack
            spacing={6}
            align="start"
            fontSize="md"
            lineHeight="tall"
            color="gray.700"
          >
            <Box>
              <Heading as="h2" fontSize="2xl" mb={3}>
                Introduction
              </Heading>
              <Text>
                Protecting your personal data is one of our highest priorities.
                This Privacy Policy outlines how LeadiumX.ai (also referred to
                as "LeadiumX", "we", "us", or "our") collects, uses, shares, and
                protects information when you use our website, platform, and
                services (collectively, the "Services"). By using our Services,
                you agree to the practices described here.
              </Text>
              <Text mt={3}>
                If you use LeadiumX.ai on behalf of a business or organization,
                then "you" includes that entity and you confirm you have
                authority to consent to these terms for it.
              </Text>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                1. Role: Controller / Processor
              </Heading>
              <Text>
                For standard users and website visitors, LeadiumX acts as a data
                controller — deciding how and why personal data is collected and
                used.
              </Text>
              <Text mt={2}>
                For enterprise/white-labeled clients who use our platform and
                supply us with end-user data, LeadiumX acts as a data processor
                — processing data on behalf of the client. In those cases, a
                separate Data Processing Addendum (DPA) governs how we handle
                that data and supersedes conflicting terms in this policy.
              </Text>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                2. Information We Collect
              </Heading>
              <Text mb={3}>
                We collect data in several categories, either voluntarily
                provided by you or automatically collected:
              </Text>

              <Heading as="h4" fontSize="lg" mb={2} fontWeight="semibold">
                2.1 Personal (Identifiable) Data
              </Heading>
              <Text mb={2}>We may collect:</Text>
              <VStack align="start" spacing={1} pl={6}>
                <Text>
                  • Name, email address, phone number, company name, address,
                  and other contact details
                </Text>
                <Text>• Credentials (username, password)</Text>
                <Text>
                  • Payment information (when you subscribe or purchase
                  services) — typically via our payment processor, so we only
                  retain minimal information needed for billing
                </Text>
                <Text>
                  • Communications Data: If you enable voice/telephony features,
                  we collect audio recordings, transcripts, call metadata,
                  interaction logs, etc.
                </Text>
                <Text>
                  • Sensitive Data: We do not collect sensitive categories (such
                  as health, race, biometrics, etc.) unless explicitly provided
                  and with your consent.
                </Text>
              </VStack>

              <Heading
                as="h4"
                fontSize="lg"
                mb={2}
                mt={4}
                fontWeight="semibold"
              >
                2.2 Usage, Device & Technical Data
              </Heading>
              <Text mb={2}>Automatically, we may collect:</Text>
              <VStack align="start" spacing={1} pl={6}>
                <Text>
                  • IP address, browser type/version, operating system
                </Text>
                <Text>• The pages you view, timestamps, session durations</Text>
                <Text>• Device identifiers, connection information</Text>
                <Text>• Cookies, tracking technologies, usage logs</Text>
                <Text>
                  • Location data (if permitted by you or your device settings)
                </Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                3. Purposes for Use & Legal Bases
              </Heading>
              <Text mb={2}>We use this information for:</Text>
              <VStack align="start" spacing={1} pl={6}>
                <Text>• Delivering and operating the Services</Text>
                <Text>• Managing your account, payments, support</Text>
                <Text>
                  • Sending you updates, marketing communications (with opt-out)
                </Text>
                <Text>• Improving and personalizing our platform</Text>
                <Text>• Security, fraud prevention, detection</Text>
                <Text>• Audits, compliance, legal obligations</Text>
              </VStack>
              <Text mt={3}>
                The lawful basis (for jurisdictions that require it) may include
                consent, performance of contract, legitimate business interests,
                or compliance with law.
              </Text>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                4. Communications & Marketing
              </Heading>
              <Text>
                You may receive emails, notifications, or promotional messages
                from LeadiumX about new features, offers, or updates. You can
                opt out at any time via unsubscribe links or by contacting us.
              </Text>
              <Text mt={3}>
                We will not rent or sell your personal information to third
                parties for marketing without your explicit consent.
              </Text>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                5. Sharing & Disclosure of Data
              </Heading>
              <Text mb={2}>
                We may share or disclose your information in these scenarios:
              </Text>
              <VStack align="start" spacing={2} pl={6}>
                <Text>
                  • With service providers and contractors who help operate the
                  platform (e.g. cloud hosting, analytics, communications) under
                  confidentiality obligations
                </Text>
                <Text>
                  • With affiliates or partners (only when necessary and in
                  compliance with this policy)
                </Text>
                <Text>
                  • In aggregated or de-identified form for analytics,
                  benchmarking, or research — such data cannot reasonably
                  identify you
                </Text>
                <Text>
                  • To comply with legal obligations, regulatory requirements,
                  or court orders
                </Text>
                <Text>
                  • To investigate or prevent fraud, abuse, security breaches,
                  or illegal activities
                </Text>
                <Text>
                  • In connection with business transactions (e.g. mergers,
                  acquisitions, reorganization) — you'll be notified when your
                  data is subject to such changes
                </Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                6. Recording, Processing & Voice Data
              </Heading>
              <VStack align="start" spacing={2}>
                <Text>
                  • For voice/telephony-enabled features, you consent that calls
                  and interactions may be recorded, transcribed, and processed
                  as needed to provide the Services, monitor quality, and
                  enhance models.
                </Text>
                <Text>
                  • We may use de-identified or aggregated voice/interaction
                  data to improve our algorithms and services, provided it
                  cannot reasonably be traced back to you.
                </Text>
                <Text>
                  • In jurisdictions where required, we will obtain consent for
                  recording or notify participants before recording begins.
                </Text>
                <Text>
                  • You may have settings controlling how much to store (e.g.
                  transcripts, recordings, logs).
                </Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                7. Cookies & Tracking Technologies
              </Heading>
              <Text>
                We use cookies, web beacons, local storage, and similar tools to
                track usage, preferences, analytics, and improve user
                experience. You can typically manage cookie settings via your
                browser (reject, block, delete). Disabling certain cookies may
                reduce functionality.
              </Text>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                8. Data Retention
              </Heading>
              <Text mb={2}>We retain your data for as long as needed to:</Text>
              <VStack align="start" spacing={1} pl={6}>
                <Text>• Provide and maintain the Services</Text>
                <Text>
                  • Comply with legal obligations (tax, accounting, litigation)
                </Text>
                <Text>• Resolve disputes and enforce our agreements</Text>
                <Text>• Conduct internal operations and analytics</Text>
              </VStack>
              <Text mt={3}>
                If deletion is requested and lawful, we will remove your
                personal data from active systems (subject to backups and legal
                retention requirements).
              </Text>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                9. Your Rights & Choices
              </Heading>
              <Text mb={2}>
                Depending on your jurisdiction, you may have rights including:
              </Text>
              <VStack align="start" spacing={1} pl={6}>
                <Text>• Access — request a copy of your personal data</Text>
                <Text>• Correction — request edits or updates</Text>
                <Text>
                  • Deletion — ask us to erase your data (with some exceptions)
                </Text>
                <Text>• Restriction — limit how your data is processed</Text>
                <Text>• Objection — oppose certain processing</Text>
                <Text>
                  • Portability — request your data in a transferable format
                </Text>
                <Text>
                  • Withdraw consent — applicable where processing is based on
                  consent
                </Text>
              </VStack>
              <Text mt={3}>
                To make a request, contact us (details below). We may require
                verification before honoring requests. Some data may not be
                deletable (e.g. required for legal, security, or operational
                reasons).
              </Text>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                10. Security Measures
              </Heading>
              <Text>
                We implement reasonable administrative, technical, and physical
                safeguards to protect your data — encryption, access controls,
                monitoring, audits, etc. In the event of a data breach affecting
                your personal information, we will notify you as required by
                law, and take remedial actions.
              </Text>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                11. Third-Party Links & Content
              </Heading>
              <Text>
                Our Services may include links or integrations to third-party
                websites, services, or content. These are governed by their own
                privacy policies. We are not responsible for their practices and
                encourage you to review the policies of any site you visit.
              </Text>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                12. International Transfers & Jurisdictions
              </Heading>
              <Text>
                If you access LeadiumX.ai from outside our home country, your
                data may be transferred to and stored on servers located outside
                your jurisdiction. By using the service, you consent to such
                cross-border transfers, subject to applicable law safeguards
                (e.g. standard contractual clauses, if applicable).
              </Text>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                13. Children & Minors
              </Heading>
              <Text>
                Our Services are not intended for persons under 13 (or higher
                age where required by local law). We do not knowingly collect
                personal data from minors. If we become aware we have collected
                data from a minor, we will take steps to delete it.
              </Text>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                14. Changes to This Policy
              </Heading>
              <Text>
                We may update this Privacy Policy occasionally. When significant
                changes occur, we will notify you (via email or in-app notice)
                before they take effect, and update the "Last Updated" date.
                Continued use after modifications means you accept the revised
                policy.
              </Text>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                15. Contact & Governing Law
              </Heading>
              <Text mb={2}>
                If you have questions, concerns, or requests regarding this
                policy or your data, contact:
              </Text>
              <Box pl={6}>
                <Text fontWeight="semibold">LeadiumX.ai</Text>
                <Text>1650 Sand Lake Road Orlando FL 32809</Text>
                <Text>
                  Email:{" "}
                  <ChakraLink
                    href="mailto:Support@leadiumx.ai"
                    color="primary.600"
                  >
                    Support@leadiumx.ai
                  </ChakraLink>
                </Text>
              </Box>
              <Text mt={3}>
                This policy is governed by the laws of the state of Florida,
                USA. except where otherwise required by applicable law.
              </Text>
            </Box>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
